import { useEffect, useState } from 'react';
import { generateFacilityReport } from '../../../../helpers/api';
import { getYears } from '../../../../helpers/dateHelpers';
import { reportTypes } from '../../../../helpers/appstate';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  IconButton,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  Chip,
  Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { CalendarMonth, Science, EditCalendarOutlined, AddAlarm } from '@mui/icons-material';
import LoaderSkeleton from '../../../common/LoaderSkeleton';
import SchedulerDialog from '../../facilities2/view-facility/scheduler-dialogs/SchedulerDialog';

export default function ConsultantFacilityQuarter({ facilityList, facilityLoading, initFacility }) {
  const years = getYears();

  const [quarterList, setQuarterList] = useState([]);
  const [quarterLoading, setQuarterLoading] = useState(false);
  const [facilitySelected, setFacilitySelected] = useState(initFacility);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [quarterSelected, setQuarterSelected] = useState(false);
  const [dialogSchedule, setDialogSchedule] = useState(false);
  const [refresh, setRefresh] = useState(false);

  useEffect(() => {
    if (facilitySelected?.facilityId && selectedYear) {
      fetchQuarters();
    }
  }, [facilitySelected, selectedYear]);

  useEffect(() => {
    if (refresh) {
      fetchQuarters();
    }
  }, [refresh, facilitySelected, selectedYear]);

  const fetchQuarters = async () => {
    setQuarterLoading(true);
    try {
      const res = await generateFacilityReport(facilitySelected?.facilityId, selectedYear);
      const { data, status } = res?.result || {};
      if (status?.code === '00') {
        setQuarterList(data || []);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setQuarterLoading(false);
      setRefresh(false);
    }
  };

  // Shared modern Card styles
  const cardStyles = {
    p: 3,
    borderRadius: '16px',
    border: '1px solid',
    borderColor: 'divider',
    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.03)',
    bgcolor: '#ffffff',
    height: '100%',
  };

  // 1. YOUR ORIGINAL CORE FUNCTIONALITY RESTORED
  const handleContentRendering = () => {
    if (quarterLoading || facilityLoading) {
      return (
        <Card elevation={0} sx={cardStyles}>
          {renderCardHeader()}
          <Box mt={4}>
            <LoaderSkeleton />
            <LoaderSkeleton />
          </Box>
        </Card>
      );
    } else {
      if (quarterList?.length < 1) {
        return '';
      } else {
        return renderContent();
      }
    }
  };

  // 2. MODERN HEADER UI
  const renderCardHeader = () => {
    return (
      <Box
        display="flex"
        flexDirection={{ xs: 'column', md: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'flex-start', md: 'center' }}
        gap={2}
        mb={3}
      >
        <Box>
          <Typography variant="h6" fontWeight={700} color="text.primary">
            {selectedYear} Monitoring Schedule
          </Typography>
          <Typography variant="body2" color="textSecondary" mt={0.5}>
            Set and update monitoring dates for Sampling Points
          </Typography>
        </Box>

        <Box display="flex" gap={2} width={{ xs: '100%', md: 'auto' }}>
          <Autocomplete
            disablePortal
            options={facilityList || []}
            value={facilitySelected}
            getOptionLabel={(option) => option?.facilityName || ''}
            onChange={(e, value) => {
              if (value) setFacilitySelected(value);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select Facility"
                InputProps={{
                  ...params.InputProps,
                  sx: { borderRadius: '10px', bgcolor: '#f9fafb' },
                }}
              />
            )}
            size="small"
            sx={{ width: { xs: '100%', sm: '250px' } }}
          />

          <Select
            value={selectedYear}
            size="small"
            onChange={(e) => setSelectedYear(e.target.value)}
            sx={{ borderRadius: '10px', bgcolor: '#f9fafb', minWidth: '100px' }}
          >
            {years?.map((year) => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
          </Select>
        </Box>
      </Box>
    );
  };

  // 3. MODERN LIST UI
  const renderContent = () => {
    return (
      <Card elevation={0} sx={cardStyles}>
        {renderCardHeader()}

        <Stack spacing={2}>
          {quarterList?.map((i, index) => {
            const isQAQC = i?.reportType === reportTypes.QAQC;
            const isDateEmpty = i?.scheduleDate?.split('T')[0] === '0001-01-01';

            return (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 2,
                  p: 2,
                  borderRadius: '12px',
                  border: '1px solid',
                  borderColor: isQAQC ? alpha('#ff9800', 0.3) : 'divider',
                  bgcolor: isQAQC ? alpha('#ff9800', 0.03) : '#fafbff',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    bgcolor: isQAQC ? alpha('#ff9800', 0.08) : '#f4f6f8',
                    borderColor: isQAQC ? alpha('#ff9800', 0.5) : '#c4c4c4',
                  },
                }}
              >
                {/* Left Side: Badge & Title */}
                <Box display="flex" alignItems="center" gap={2}>
                  <Box
                    sx={{
                      px: 2,
                      py: 1,
                      borderRadius: '8px',
                      bgcolor: isQAQC ? alpha('#ff9800', 0.15) : alpha('#1976d2', 0.1),
                      color: isQAQC ? '#ed6c02' : '#1976d2',
                      fontWeight: 700,
                      fontSize: '0.85rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {isQAQC && <Science fontSize="small" />}Q{i?.reportQuarter}
                  </Box>

                  <Box>
                    <Typography variant="body1" fontWeight={600} color="text.primary">
                      {i?.reportTitle || `Quarter ${i?.reportQuarter} Report`}
                    </Typography>
                    {isQAQC && (
                      <Typography variant="caption" color="warning.main" fontWeight={500}>
                        Reserved for QAQC
                      </Typography>
                    )}
                  </Box>
                </Box>

                {/* Right Side: Actions (Only show if NOT QAQC) */}
                {!isQAQC && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {isDateEmpty ? (
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<AddAlarm />}
                        onClick={() => {
                          setQuarterSelected(i);
                          setDialogSchedule(true);
                        }}
                        sx={{
                          borderRadius: '8px',
                          textTransform: 'none',
                          fontWeight: 600,
                          bgcolor: 'primary.main',
                          boxShadow: 'none',
                          '&:hover': { boxShadow: 'none', bgcolor: 'primary.dark' },
                        }}
                      >
                        Schedule
                      </Button>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          icon={<CalendarMonth fontSize="small" />}
                          label={`Scheduled: ${new Date(i?.scheduleDate).toLocaleDateString(
                            'en-NG',
                            {
                              dateStyle: 'medium',
                            }
                          )}`}
                          variant="outlined"
                          sx={{
                            borderRadius: '8px',
                            borderColor: 'divider',
                            bgcolor: '#ffffff',
                            color: 'text.secondary',
                            fontWeight: 500,
                          }}
                        />
                        <Tooltip title="Reschedule Date">
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={() => {
                              setQuarterSelected(i);
                              setDialogSchedule(true);
                            }}
                            sx={{
                              bgcolor: alpha('#1976d2', 0.05),
                              '&:hover': { bgcolor: alpha('#1976d2', 0.15) },
                            }}
                          >
                            <EditCalendarOutlined fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            );
          })}
        </Stack>
      </Card>
    );
  };

  return (
    <Box>
      <Box>{handleContentRendering()}</Box>

      {dialogSchedule && (
        <SchedulerDialog
          open={dialogSchedule}
          onClose={() => {
            setDialogSchedule(false);
            setRefresh(true);
          }}
          facilityId={facilitySelected?.facilityId}
          quarter={quarterSelected}
          year={selectedYear}
        />
      )}
    </Box>
  );
}
